import React, { useCallback, useEffect, useRef, useState } from "react";
import { X, Loader2, AlertTriangle, CheckCircle2, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "@/utils/api";
import ShipmentEditModal from "@/components/ShipmentEditModal";
import { useAuth } from "@/context/AuthContext";

const Row = ({ k, v }) => (<div className="flex justify-between gap-3 text-xs py-0.5"><span className="text-slate-500">{k}</span><span className="font-semibold text-slate-800 text-right break-words max-w-[65%]">{v ?? "—"}</span></div>);

/** Nothing goes to Delhivery until a person has looked at this screen and approved it. */
export default function ManifestReviewModal({ shipment, onClose, onApproved }) {
  const [chk, setChk] = useState(null);
  const [verified, setVerified] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const { user } = useAuth();

  const closeRef = useRef(onClose);
  closeRef.current = onClose; // parent re-renders often; a changing onClose must not reload the review and un-tick the checkbox
  const load = useCallback(async () => {
    setChk(null); setVerified(false);
    try { setChk((await api.get(`/v2/manifest-gate/${shipment.id}/check`)).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load the review"); closeRef.current(); }
  }, [shipment.id]);
  useEffect(() => { load(); }, [load]);

  const approve = async () => {
    setBusy(true);
    try { await api.post(`/v2/manifest-gate/${shipment.id}/approve`); await onApproved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not approve"); }
    setBusy(false);
  };

  const toggleOwnerRisk = async (on) => {
    if (on && !window.confirm("Send EVERY Delhivery B2B booking as Owner Risk? Delhivery will not insure the goods or charge the 0.4% fee. Only do this if Sendtoo has its own open transit insurance covering these shipments.")) return;
    try { await api.put("/v2/manifest-gate/settings", { force_owner_risk: on }); toast.success(on ? "All B2B bookings now go to Delhivery as Owner Risk" : "Back to the customer's chosen insurance"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not change the setting"); }
  };
  const s = chk?.summary;
  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 overflow-auto p-4" onClick={onClose}>
        <div className="bg-white rounded-xl w-full max-w-2xl my-4 shadow-2xl flex flex-col max-h-[92vh]" onClick={(e) => e.stopPropagation()} data-testid="manifest-review-modal">
          <div className="flex items-center justify-between p-4 border-b shrink-0">
            <h3 className="font-bold text-slate-800">Manifest review — {shipment.order_id || shipment.tracking_id}</h3>
            <button onClick={onClose}><X className="w-5 h-5 text-slate-500" /></button>
          </div>
          {!chk ? <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
            <div className="flex flex-col min-h-0 flex-1"><div className="p-4 space-y-4 overflow-y-auto flex-1">
              <p className="text-xs text-slate-500">Check these details carefully. Once you approve, they are sent to Delhivery exactly as shown.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="border rounded-lg p-3">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">Goods</h4>
                  <Row k="Service" v={`${s.carrier || ""} · ${s.class}`} />
                  <Row k="Boxes" v={s.box_count} />
                  <Row k="Chargeable weight" v={`${s.chargeable_weight} kg`} />
                  {s.delhivery_declared_weight_kg && <Row k="Declared to Delhivery" v={`${s.delhivery_declared_weight_kg} kg (B2B minimum - billed on actual weight)`} />}
                  <Row k="Goods value" v={s.goods_value != null ? `₹${s.goods_value}` : "—"} />
                  {s.delhivery_risk && <Row k="Sent to Delhivery as" v={s.delhivery_risk === "OWNER_RISK" ? `Owner Risk${s.owner_risk_forced ? " (forced - no 0.4% fee)" : ""}` : `Carrier Risk (fee ≈ ₹${s.rov_fee_estimate})`} />}
                  <Row k="Description" v={s.description} />
                  <Row k="Invoice no." v={s.invoice_number} />
                  <Row k="E-way bill" v={s.eway_bill_number} />
                  {s.handoff_mode && <Row k="Handoff" v={s.handoff_mode} />}
                </div>
                <div className="border rounded-lg p-3">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">Route</h4>
                  <Row k="Pickup" v={`${s.origin?.name || ""}, ${s.origin?.city || ""} ${s.origin?.pincode || ""}`} />
                  <Row k="Pickup phone" v={s.origin?.phone} />
                  <Row k="Deliver to" v={`${s.destination?.name || ""}, ${s.destination?.city || ""} ${s.destination?.pincode || ""}`} />
                  <Row k="Delivery phone" v={s.destination?.phone} />
                  <Row k="Pickup location" v={s.warehouse} />
                </div>
              </div>
              {s.boxes?.length > 0 && (
                <div>
                  <table className="w-full text-xs border rounded">
                    <thead className="bg-slate-50 text-slate-500"><tr><th className="text-left p-1.5">Box</th><th>L × B × H (cm)</th><th>Weight (kg)</th></tr></thead>
                    <tbody>{(showAll ? s.boxes : s.boxes.slice(0, 2)).map((b) => (<tr key={b.no} className="border-t text-center"><td className="text-left p-1.5">{b.no}</td><td>{b.l || "—"} × {b.b || "—"} × {b.h || "—"}</td><td>{b.weight || "—"}</td></tr>))}</tbody>
                  </table>
                  {s.boxes.length > 2 && (
                    <button onClick={() => setShowAll((v) => !v)} className="mt-1 text-xs font-semibold text-brand-600 hover:underline" data-testid="review-toggle-boxes">
                      {showAll ? "Show fewer boxes" : `View all ${s.boxes.length} boxes (${s.boxes.length - 2} more)`}
                    </button>
                  )}
                  <div className="text-[11px] text-slate-500 mt-0.5">{s.boxes.length} boxes · total dead weight {s.dead_weight_total ?? "—"} kg</div>
                </div>
              )}
              {chk.issues.length > 0 && (
                <div className="space-y-1.5" data-testid="manifest-issues">
                  {chk.issues.map((i, n) => (
                    <div key={n} className={`flex gap-2 text-xs rounded p-2 border ${i.level === "block" ? "bg-red-50 border-red-200 text-red-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
                      <AlertTriangle className="w-4 h-4 shrink-0" /><span><b>{i.level === "block" ? "Fix before manifesting: " : "Check: "}</b>{i.message}</span>
                    </div>
                  ))}
                </div>
              )}
              {chk.issues.length === 0 && <div className="flex gap-2 text-xs rounded p-2 border bg-emerald-50 border-emerald-200 text-emerald-800"><CheckCircle2 className="w-4 h-4" /> No problems found.</div>}
              {user?.role === "admin" && (
                <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer" data-testid="owner-risk-toggle">
                  <input type="checkbox" className="mt-0.5 accent-brand-600" checked={!!s.owner_risk_forced} onChange={(e) => toggleOwnerRisk(e.target.checked)} />
                  <span>Admin: send <b>all</b> Delhivery B2B bookings as Owner Risk (no 0.4% Delhivery fee; Sendtoo carries the risk under its own policy).</span>
                </label>
              )}
              <button onClick={() => setEditing(true)} className="text-sm font-semibold text-brand-600 flex items-center gap-1.5 hover:underline" data-testid="review-edit">
                <Pencil className="w-4 h-4" /> Something wrong? Edit shipment details
              </button>
              </div>
              <div className="border-t p-4 space-y-3 shrink-0 bg-white rounded-b-xl">
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="checkbox" className="mt-1 accent-brand-600" checked={verified} disabled={chk.blockers > 0} onChange={(e) => setVerified(e.target.checked)} data-testid="review-verified" />
                <span>I have checked the boxes, weights, addresses, value and e-way bill above.</span>
              </label>
              <div className="flex justify-end gap-2">
                <button onClick={onClose} className="px-4 py-2 text-sm text-slate-500 font-semibold">Cancel</button>
                <button onClick={approve} disabled={busy || !verified || chk.blockers > 0} data-testid="review-approve-manifest"
                  className="px-5 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-40">
                  {busy ? "Sending to Delhivery..." : "Approve & send to Delhivery"}
                </button>
              </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {editing && <ShipmentEditModal shipmentId={shipment.id} onClose={() => setEditing(false)} onSaved={load} />}
    </>
  );
}
